import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface Enquiry {
  id: string;
  product_name: string | null;
  vehicle: string | null;
  subject: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  image_url: string | null;
  status: string;
  created_at: string;
}

const STATUSES = ["new", "contacted", "resolved"];

const statusStyle: Record<string, string> = {
  new: "bg-brand-gold/20 text-brand-dark",
  contacted: "bg-neutral-200 text-neutral-700",
  resolved: "bg-green-100 text-green-700",
};

const SPECIAL_ORDER_SUBJECT = "Special Order / Part Request";
const isSpecialOrder = (e: Enquiry) => e.subject === SPECIAL_ORDER_SUBJECT;

export default function EnquiriesManager() {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("enquiries")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (err) throw err;
      setEnquiries((data as Enquiry[]) || []);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = enquiries.filter((e) => filter === "All" || e.status === filter);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((e) => e.id));
    });
  };

  const setStatus = async (id: string, status: string) => {
    const enquiry = enquiries.find((e) => e.id === id);
    const { error: err } = await supabase.from("enquiries").update({ status }).eq("id", id);
    if (err) {
      setError(err.message);
      return;
    }
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));

    // Automatically email the customer when their enquiry moves to
    // "Contacted" or "Resolved".
    if (enquiry && (status === "contacted" || status === "resolved") && enquiry.email) {
      supabase.functions
        .invoke("send-enquiry-email", {
          body: {
            type: "status",
            status,
            name: enquiry.name,
            email: enquiry.email,
            subject: enquiry.subject,
            product_name: enquiry.product_name,
            vehicle: enquiry.vehicle,
          },
        })
        .catch(() => {});
    }
  };

  const deleteOne = async (id: string) => {
    const { error: err } = await supabase
      .from("enquiries")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);
    if (err) {
      setError(err.message);
      return;
    }
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
  };

  const bulkDelete = async () => {
    const { error: err } = await supabase
      .from("enquiries")
      .update({ deleted_at: new Date().toISOString() })
      .in("id", Array.from(selected));
    setConfirmBulk(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSelected(new Set());
    load();
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">Enquiries</h2>
          <p className="mt-1 text-sm text-neutral-500">{enquiries.length} total enquiries.</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
        >
          <option value="All">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError("")} className="text-red-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="mt-4 flex items-center justify-between rounded-md bg-brand-dark px-4 py-3 text-white">
          <span className="text-sm">{selected.size} selected</span>
          <button
            onClick={() => setConfirmBulk(true)}
            className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            Bulk Delete
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="h-4 w-4 accent-brand-dark"
                  />
                </th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Part / Subject</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <>
                  <tr
                    key={e.id}
                    className={`border-b border-neutral-100 ${
                      isSpecialOrder(e)
                        ? "bg-brand-gold/10 hover:bg-brand-gold/15"
                        : "hover:bg-neutral-50"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggleSelect(e.id)}
                        className="h-4 w-4 accent-brand-dark"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-brand-dark">{e.name || "—"}</p>
                      <p className="text-xs text-neutral-500">{e.email || ""}</p>
                      {e.image_url && (
                        <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-brand-gold">
                          <i className="ri-image-line"></i> Photo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-neutral-700">{e.product_name || e.subject || "General"}</p>
                        {isSpecialOrder(e) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-brand-dark whitespace-nowrap">
                            <i className="ri-flag-2-line"></i>
                            Special Order
                          </span>
                        )}
                      </div>
                      {e.vehicle && <p className="text-xs text-neutral-500">{e.vehicle}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={e.status}
                        onChange={(ev) => setStatus(e.id, ev.target.value)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium focus:outline-none ${statusStyle[e.status] || ""}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{formatDate(e.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100"
                          title="View"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        <button
                          onClick={() => deleteOne(e.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-red-500 hover:bg-red-50"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr key={`${e.id}-detail`} className="border-b border-neutral-100 bg-neutral-50">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-neutral-400">Phone</p>
                            <p className="text-neutral-700">{e.phone || "—"}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-neutral-400">Subject</p>
                            <p className="text-neutral-700">{e.subject || "—"}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-neutral-400">Email</p>
                            <a href={`mailto:${e.email}`} className="text-brand-gold hover:underline">
                              {e.email || "—"}
                            </a>
                          </div>
                          <div className="sm:col-span-2 lg:col-span-3">
                            <p className="text-xs uppercase tracking-wide text-neutral-400">Message</p>
                            <p className="whitespace-pre-wrap text-neutral-700">{e.notes || "No message."}</p>
                          </div>
                          {e.image_url && (
                            <div className="sm:col-span-2 lg:col-span-3">
                              <p className="text-xs uppercase tracking-wide text-neutral-400">Part Photo</p>
                              <a href={e.image_url} target="_blank" rel="nofollow noreferrer">
                                <img
                                  src={e.image_url}
                                  alt="Part attached to this enquiry"
                                  className="mt-1 h-44 w-60 rounded-md border border-neutral-200 bg-white object-cover"
                                />
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-neutral-400">
                    No enquiries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-brand-dark">
              Delete {selected.size} enquiries?
            </h3>
            <p className="mt-2 text-sm text-neutral-500">They'll be moved to Trash and can be restored.</p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setConfirmBulk(false)}
                className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={bulkDelete}
                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}